import { setCookie } from '@opten/cookie/set';
import {parseCssUnit} from '@opten/number/parse-css-unit';
import {getCss }from '@opten/dom/get-css';
import {addClass} from '@opten/dom/add-class';
import {easeInOutQuad} from '@opten/animation/ease-in-out-quad';
import {eventNamespaceFactory} from '@opten/events/event-namespace-factory';
export class CookieBannerOptions {
    className?: string;
    approveClassName?: string;
    showAfter: number;
    animationDuration?: number;
    // closeClassName?: string;
    cookie: {
        expirationInDays?: number;
        domain?: string;
        path?: string;
        secure?: boolean;
    };
}

export class CookieBanner {
    private _options: CookieBannerOptions = {
        className: 'js-cookie-consent',
        approveClassName: 'js-cookie-consent-approve',
        showAfter: 600,
        animationDuration: 350,
        // closeClassName: 'js-cookie-consent-close',
        cookie: {
            expirationInDays: 356,
            secure: false
        }
    };

    private _cookieKey: string;
    private _height: number;
    private _attributeName: string = 'data-cookie-consent';

    // prevent multiple resizings to add more height
    // if it's the same height as before!
    private _cachedHeight: number | null = null;

    private _boundResize = this._handleResize.bind(this);

    private _eventNamespaceFn = eventNamespaceFactory('merbag.cookie-consent');

    constructor(
        private _stickyElements: Array<HTMLElement>,
        options: CookieBannerOptions
    ) {
        this._options = { ...this._options, ...options };

        if (this._options.showAfter) {
            setTimeout(() => this._init(), this._options.showAfter);
        } else {
            this._init();
        }
    }

    private _init() {
        const banner = this._getBanner();

        if (banner) {
            this._cookieKey = banner.getAttribute('data-cookie-key');

            if (this._cookieKey) {
                Array.from(
                    document.getElementsByClassName(
                        this._options.approveClassName
                    )
                ).forEach(button =>
                    button.addEventListener('click', event =>
                        this._handleClick(event, true)
                    )
                );

                this._eventNamespaceFn(banner, 'show');

                this._animate(
                    banner,
                    0,
                    banner.offsetHeight,
                    this._options.animationDuration,
                    () => {
                        this._handleResize();

                        window.addEventListener('resize', this._boundResize);

                        this._eventNamespaceFn(banner, 'shown');
                    }
                );

                /*Array.from(
                    document.getElementsByClassName(
                        this._options.closeClassName
                    )
                ).forEach(button =>
                    button.addEventListener('click', event =>
                        this._handleClick(event, false)
                    )
                );*/

                /* addClass(banner, '--show');
                this._handleResize();
                window.addEventListener('resize', this._boundResize);*/
            }
        }
    }

    private _animate(
        banner: HTMLElement,
        start: number,
        distance: number,
        duration: number,
        complete: Function
    ) {
        if (banner) {
            // todo: function in @opten/events?
            let timeStart: number;
            let timeElapsed: number;

            const loop = time => {
                timeElapsed = time - timeStart;

                banner.style.height = `${easeInOutQuad(
                    timeElapsed,
                    start,
                    distance,
                    duration
                )}px`;

                // todo: encapsulate?
                this._handleResize();

                if (timeElapsed < duration) {
                    requestAnimationFrame.call(window, loop);
                } else {
                    banner.style.height = null;
                    complete();
                }
            };

            requestAnimationFrame.call(window, time => {
                timeStart = time;
                // todo: encapsulate?
                addClass(banner, '--show');
                loop(time);
            });
        }
    }

    private _getBanner(): HTMLElement {
        return document.getElementsByClassName(
            this._options.className
        )[0] as HTMLElement;
    }

    private _update(banner?: HTMLElement) {
        if (banner) {
            this._setSticky(document.body, 'margin-bottom', false);
            this._setElements(false);
        } else {
            this._setSticky(document.body, 'margin-bottom', true);
            this._setElements(true);
        }
    }

    private _setElements(remove: boolean) {
        if (this._stickyElements) {
            this._stickyElements.forEach(sticky =>
                this._setSticky(sticky, 'bottom', remove)
            );
        }
    }

    private _setSticky(sticky: HTMLElement, style: string, remove: boolean) {
        const attributeName = `${this._attributeName}-${style}`;

        if (!remove) {
            const calculated = parseCssUnit(getCss(sticky, style));
            sticky.setAttribute(attributeName, calculated.toString());
            sticky.style[style] = `${calculated + this._height}px`;
        } else if (sticky.hasAttribute(attributeName)) {
            const calculated = Number(sticky.getAttribute(attributeName));

            if (calculated && calculated > 0) {
                sticky.style[style] = `${calculated}px`;
            } else {
                sticky.style[style] = null;
            }

            sticky.removeAttribute(attributeName);
        }
    }

    private _handleClick(event: Event, setCookie: boolean) {
        event.preventDefault();

        if (setCookie) {
            this._setCookie();
        }

        const banner = this._getBanner();

        if (banner) {
            this._eventNamespaceFn(banner, 'hide');

            // todo: animation?
            // todo: kill events?
            banner.parentElement.removeChild(banner);

            this._eventNamespaceFn(banner, 'hidden');

            // remove attributes, height, ...
            this._update();

            // kills events
            window.removeEventListener('resize', this._boundResize);
        }
    }

    private _setCookie(): void {
        if (!this._cookieKey) {
            throw new Error(
                `Cookie-Consent: Cannot set Cookie, no key defined!`
            );
        }

        const date = new Date();
        date.setDate(
            date.getDate() + (this._options.cookie.expirationInDays || 365)
        );

        setCookie(
            this._cookieKey,
            'true',
            date,
            this._options.cookie.path,
			this._options.cookie.domain,
			this._options.cookie.secure
        );
    }

    private _handleResize() {
        const banner = this._getBanner();

        if (banner) {
            this._height = Math.round(banner.getBoundingClientRect().height);

            if (this._cachedHeight !== this._height) {
                this._setSticky(document.body, 'margin-bottom', true);
                this._setElements(true);

                this._cachedHeight = this._height;
                this._update(banner);

                this._eventNamespaceFn(banner, 'resize');
            }
        }
    }
}
